// Spec 007-vehicle-transfer-flow — TransferPendingBadge component.
//
// Inline pending-state card on VehicleDetail. Renders when the parent has
// determined a pending transfer exists for the vehicle (via useTransferStatus).
// Shows the code prominently, an expiry countdown, copy/share affordances,
// and a Cancel button that fires useCancelTransfer.
//
// PRIVACY: this component handles the outgoing owner's view; no incoming-owner
// identity is known yet (pending state precedes acceptance). No `firebase/*`
// imports — cancel routes through the gg-app endpoint via useCancelTransfer.
// See specs/007-vehicle-transfer-flow/contracts/component-api.md Contract 1.

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import { useCancelTransfer } from '@/hooks/useCancelTransfer';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';

export interface TransferPendingBadgeProps {
  transferId: string;
  code: string;
  expiresAt: string;
}

export function TransferPendingBadge({ transferId, code, expiresAt }: TransferPendingBadgeProps) {
  const { mutate: cancel, isPending } = useCancelTransfer();

  // Re-render every 30s so the relative expiry countdown stays fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const expiry = formatDistanceToNow(new Date(expiresAt), { addSuffix: true });

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canCopy =
    typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';

  const handleCopy = () => {
    if (canCopy) {
      void navigator.clipboard.writeText(code);
    }
  };

  const handleShare = () => {
    if (canShare) {
      void navigator.share({ text: code, title: 'Absolute Pinks transfer code' });
    }
  };

  return (
    <Card accent="orange" style={{ marginTop: SPACE.md }}>
      <div style={{ color: T.textMuted, fontSize: 12, letterSpacing: '0.04em' }}>
        TRANSFER PENDING
      </div>
      <div
        style={{
          fontFamily: F.mono,
          fontSize: 32,
          letterSpacing: '0.1em',
          color: T.text,
          marginTop: SPACE.sm,
          userSelect: 'all',
        }}
      >
        {code}
      </div>
      <div style={{ color: T.textMuted, fontSize: 13, marginTop: SPACE.sm }}>Expires {expiry}</div>
      <div
        style={{
          display: 'flex',
          gap: SPACE.sm,
          marginTop: SPACE.md,
          flexWrap: 'wrap',
        }}
      >
        <Button onClick={handleCopy} type="button">
          Copy
        </Button>
        {canShare ? (
          <Button onClick={handleShare} type="button">
            Share
          </Button>
        ) : null}
        <Button
          variant="danger"
          onClick={() => cancel(transferId)}
          disabled={isPending}
          loading={isPending}
          aria-busy={isPending || undefined}
          type="button"
        >
          {isPending ? 'Canceling…' : 'Cancel transfer'}
        </Button>
      </div>
    </Card>
  );
}
